
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { DollarSign, CalendarClock, Utensils, Star } from 'lucide-react';

interface PlaceStatsProps {
  place: any;
  isEditing: boolean;
  onInputChange: (field: string, value: any) => void;
}

export function PlaceStats({ place, isEditing, onInputChange }: PlaceStatsProps) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-purple-50">
            <DollarSign className="w-5 h-5 text-purple-600" />
            {isEditing ? (
              <Select
                value={place.price_range || ''}
                onValueChange={(value) => onInputChange('price_range', value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select price range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="budget">Budget</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="expensive">Expensive</SelectItem>
                  <SelectItem value="luxury">Luxury</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <span className="text-sm font-medium">
                {getPriceRangeDisplay(place.price_range)}
              </span>
            )}
          </div>

          <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-green-50">
            <CalendarClock className="w-5 h-5 text-green-600" />
            {isEditing ? (
              <Select
                value={place.requires_reservation ? "yes" : "no"}
                onValueChange={(value) => onInputChange('requires_reservation', value === "yes")}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Reservation required?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Required</SelectItem>
                  <SelectItem value="no">Not Required</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              place.requires_reservation && (
                <span className="text-sm font-medium">Reservation Required</span>
              )
            )}
          </div>

          {place.cuisine_type && (
            <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-orange-50">
              <Utensils className="w-5 h-5 text-orange-600" />
              {isEditing ? (
                <Input
                  value={place.cuisine_type}
                  onChange={(e) => onInputChange('cuisine_type', e.target.value)}
                  className="text-center"
                  placeholder="Cuisine type"
                />
              ) : (
                <span className="text-sm font-medium">{place.cuisine_type}</span>
              )}
            </div>
          )}

          {place.rating && (
            <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-yellow-50">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <div className="text-center">
                {isEditing ? (
                  <Input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={place.rating}
                    onChange={(e) => onInputChange('rating', parseFloat(e.target.value))}
                    className="text-center w-20"
                  />
                ) : (
                  <>
                    <div className="text-lg font-semibold">{place.rating.toFixed(1)}</div>
                    <div className="text-sm text-muted-foreground">
                      {place.rating_count} {place.rating_count === 1 ? 'rating' : 'ratings'}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function getPriceRangeDisplay(range: string) {
  switch (range) {
    case 'budget': return '💰';
    case 'moderate': return '💰💰';
    case 'expensive': return '💰💰💰';
    case 'luxury': return '💰💰💰💰';
    default: return null;
  }
}
